import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, Plus, ArrowUpRight, Sparkles, Trash2, CopyX } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Loader, ErrorState, EmptyState } from "@/components/states";
import { useConfirm } from "@/context/ConfirmContext";

const formatDate = (value) =>
  value ? new Date(value).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "Not saved yet";

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-500 dark:bg-elevated dark:text-muted-foreground",
  recording: "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
  analyzing: "bg-yellow-50 text-yellow-600 dark:bg-yellow-500/10 dark:text-yellow-400",
  ready: "bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400",
  published: "bg-accent/10 text-accent",
};

export default function Content() {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const [items, setItems] = useState([]);
  const [learnings, setLearnings] = useState(null);
  const [state, setState] = useState({ loading: true, error: null });
  const [deletingId, setDeletingId] = useState(null);
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const [contentRes, learningsRes] = await Promise.all([
        api.get("/content"),
        api.get("/analytics/learnings").catch(() => ({ data: null })),
      ]);
      setItems(contentRes.data.content || []);
      setLearnings(learningsRes.data);
      setState({ loading: false, error: null });
    } catch (error) {
      setState({ loading: false, error: apiErrorMessage(error) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createDraft = async () => {
    // If a blank untitled draft already exists, navigate to it instead of creating duplicates
    const existingBlank = items.find(
      (item) => (item.title || "").trim().toLowerCase() === "untitled draft" && !(item.body || "").trim()
    );
    if (existingBlank) {
      toast.info("Opening existing untitled draft");
      navigate(`/content/${existingBlank.id}`);
      return;
    }

    try {
      const { data } = await api.post("/content", { title: "Untitled draft", body: "", platforms: [] });
      navigate(`/content/${data.id}`);
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
  };

  const deleteDraft = async (item, e) => {
    e.stopPropagation();
    const title = item.title || "Untitled draft";
    const ok = await confirm({
      title: `Delete "${title}"?`,
      description: "This draft will be permanently removed.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;

    setDeletingId(item.id);
    try {
      await api.delete(`/content/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Draft deleted");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setDeletingId(null);
    }
  };

  const duplicateKeyCounts = useMemo(() => {
    const counts = {};
    for (const item of items) {
      const key = (item.title || "Untitled draft").trim().toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [items]);

  const hasDuplicates = useMemo(() => {
    return Object.values(duplicateKeyCounts).some((count) => count > 1);
  }, [duplicateKeyCounts]);

  const cleanDuplicates = async () => {
    const ok = await confirm({
      title: "Clean up redundant duplicate drafts?",
      description: "The best draft in each duplicate group will be preserved. Redundant empty duplicates will be deleted.",
      confirmText: "Clean duplicates",
      destructive: false,
    });
    if (!ok) return;
    setCleaningDuplicates(true);
    try {
      const { data } = await api.post("/content/deduplicate");
      if (data.deleted_count > 0) {
        toast.success(`Removed ${data.deleted_count} duplicate draft(s)`);
        load();
      } else {
        toast.info("No redundant duplicate drafts found to clean");
      }
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setCleaningDuplicates(false);
    }
  };

  const hasLearnings = learnings && learnings.reason === "ok" && (learnings.learnings || []).length > 0;

  return (
    <AppShell title="Content">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-accent">Your workspace</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-foreground" data-testid="content-page-title">Content</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Ranked by your Creator DNA and real evidence.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasDuplicates && (
            <Button
              variant="outline"
              size="sm"
              onClick={cleanDuplicates}
              disabled={cleaningDuplicates}
              className="rounded-full text-xs text-muted-foreground hover:text-foreground hover:border-accent/40"
              data-testid="content-clean-duplicates-button"
            >
              <CopyX className="mr-1.5 h-3.5 w-3.5 text-accent" />
              {cleaningDuplicates ? "Cleaning…" : "Clean duplicates"}
            </Button>
          )}
          <Button onClick={createDraft} data-testid="content-new-button"
            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm">
            <Plus className="mr-2 h-4 w-4" /> New content
          </Button>
        </div>
      </div>

      {/* Learning strip */}
      {hasLearnings && (
        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-accent/25 bg-accent/5 p-4 sm:flex-row sm:items-center sm:justify-between"
          data-testid="content-channel-learning-strip">
          <div className="flex items-start gap-3 sm:items-center">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-accent/20 text-accent">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-accent">Learning Insight: </span>
              <span className="text-xs font-medium text-foreground">{learnings.learnings[0]}</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" asChild className="shrink-0 rounded-full text-xs text-accent hover:text-accent">
            <Link to="/analytics">View Analytics <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
          </Button>
        </div>
      )}

      {state.loading ? <Loader label="Loading content" /> :
       state.error ? <ErrorState message={state.error} onRetry={load} /> :
       items.length === 0 ? (
        <EmptyState icon={FileText} testid="content-empty"
          title="No content yet"
          description="Start with an idea or open a blank draft."
          action={<Button onClick={createDraft} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="content-empty-new-button">Create a draft</Button>} />
       ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="content-list">
          {items.map((item) => {
            const key = (item.title || "Untitled draft").trim().toLowerCase();
            const isDuplicate = (duplicateKeyCounts[key] || 0) > 1;

            return (
              <div
                key={item.id}
                onClick={() => navigate(`/content/${item.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/content/${item.id}`);
                  }
                }}
                className="group relative cursor-pointer rounded-2xl border border-border bg-surface p-4 sm:p-5 text-left shadow-sm transition-all hover:border-accent/40 hover:shadow-md min-w-0"
                data-testid={`content-list-item-${item.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-display text-base font-bold text-foreground leading-snug line-clamp-2">
                    {item.title || "Untitled draft"}
                  </h2>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => deleteDraft(item, e)}
                      disabled={deletingId === item.id}
                      className="rounded-full p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors focus:outline-none focus:ring-2 focus:ring-destructive/30"
                      title="Delete draft"
                      aria-label={`Delete ${item.title || "draft"}`}
                      data-testid={`content-delete-${item.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <div className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
                      <ArrowUpRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                      STATUS_COLORS[item.status] || STATUS_COLORS.draft
                    }`}
                    data-testid={`content-status-${item.id}`}
                  >
                    {item.status}
                  </span>
                  {isDuplicate && (
                    <span
                      className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400"
                      title="Multiple drafts exist with this title"
                    >
                      Duplicate
                    </span>
                  )}
                  {(item.platforms || []).map((platform) => (
                    <span
                      key={platform}
                      className="rounded-full bg-elevated px-2.5 py-0.5 text-[10px] text-muted-foreground"
                      data-testid={`content-platform-${item.id}-${platform}`}
                    >
                      {platform}
                    </span>
                  ))}
                </div>

                <p className="mt-4 text-xs text-muted-foreground" data-testid={`content-updated-${item.id}`}>
                  Updated {formatDate(item.updated_at || item.created_at)}
                </p>
              </div>
            );
          })}
        </div>
       )}
    </AppShell>
  );
}
